import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, CurrencyCircleDollar, ChartLineUp, ShieldCheck, Prohibit, Trash, CheckCircle, Clock, X, PencilSimple, ArrowsClockwise, Receipt
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function Pill({ status }) {
  const map = {
    admin:         { label: "Admin", cls: "bg-[#3A5A40] text-white" },
    active:        { label: "Aktif", cls: "bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]" },
    trial:         { label: "Deneme", cls: "bg-[#FFF3E0] text-[#E65100] border border-[#FFCC80]" },
    trial_expired: { label: "Deneme bitti", cls: "bg-[#FDECEA] text-[#C62828] border border-[#EF9A9A]" },
    expired:       { label: "Süresi bitti", cls: "bg-[#FDECEA] text-[#C62828] border border-[#EF9A9A]" },
    blocked:       { label: "Engelli", cls: "bg-[#3E3E3E] text-white" },
  };
  const v = map[status] || { label: status, cls: "bg-muted text-foreground" };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${v.cls}`}>{v.label}</span>;
}

export default function AdminPage() {
  const { user } = useAuth();

  if (user === null) return null;
  if (!user || user?.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#3A5A40] text-white flex items-center justify-center">
          <ShieldCheck size={20} weight="duotone" />
        </div>
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Yönetici Paneli</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Kullanıcılar, abonelik, fiyatlandırma ve istatistikler.</p>
        </div>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={14} weight="duotone" className="mr-1.5" /> Kullanıcılar</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="admin-tab-pricing"><CurrencyCircleDollar size={14} weight="duotone" className="mr-1.5" /> Fiyatlandırma</TabsTrigger>
          <TabsTrigger value="stats" data-testid="admin-tab-stats"><ChartLineUp size={14} weight="duotone" className="mr-1.5" /> İstatistikler</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="pricing" className="mt-4"><PricingTab /></TabsContent>
        <TabsContent value="stats" className="mt-4"><StatsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// -------- Users Tab --------
function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [extendTarget, setExtendTarget] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch { toast.error("Kullanıcılar yüklenemedi."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return users;
    return users.filter((u) => (u.email || "").toLowerCase().includes(qq) || (u.name || "").toLowerCase().includes(qq));
  }, [q, users]);

  async function block(u) {
    if (!window.confirm(`${u.email} engellensin mi? Hesap anında kilitlenecek.`)) return;
    try { await api.post(`/admin/users/${u.id}/block`); toast.success("Engellendi."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Başarısız."); }
  }
  async function unblock(u) {
    try { await api.post(`/admin/users/${u.id}/unblock`); toast.success("Engel kaldırıldı."); load(); }
    catch { toast.error("Başarısız."); }
  }
  async function makeAdmin(u) {
    if (!window.confirm(`${u.email} yönetici yapılsın mı?`)) return;
    try { await api.post(`/admin/users/${u.id}/make-admin`); toast.success("Admin oldu."); load(); }
    catch { toast.error("Başarısız."); }
  }
  async function removeAdmin(u) {
    if (!window.confirm(`${u.email} kullanıcısının admin yetkisi kaldırılsın mı?`)) return;
    try { await api.post(`/admin/users/${u.id}/remove-admin`); toast.success("Admin yetkisi kaldırıldı."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Başarısız."); }
  }
  async function del(u) {
    if (!window.confirm(`${u.email} ve TÜM verileri KALICI olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Silindi."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Başarısız."); }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Ad veya e-posta ile ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
          data-testid="admin-users-search"
        />
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-users-refresh">
          <ArrowsClockwise size={14} className="mr-1" /> Yenile
        </Button>
        <div className="text-sm text-muted-foreground ml-auto">{filtered.length} / {users.length} kullanıcı</div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">Yükleniyor…</div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Kullanıcı</th>
                <th className="text-left px-4 py-2">Durum</th>
                <th className="text-left px-4 py-2">Abonelik / Deneme</th>
                <th className="text-left px-4 py-2">Öğrenci / Ders</th>
                <th className="text-left px-4 py-2">Kayıt</th>
                <th className="text-right px-4 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3 min-w-0">
                    <div className="font-medium truncate">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </td>
                  <td className="px-4 py-3"><Pill status={u.plan_status} /></td>
                  <td className="px-4 py-3 text-xs">
                    {u.subscription_ends_at ? (
                      <div>Abonelik: <b>{formatDate(u.subscription_ends_at)}</b></div>
                    ) : u.trial_ends_at ? (
                      <div>Deneme bitiş: <b>{formatDate(u.trial_ends_at)}</b></div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{u.student_count} öğrenci · {u.lesson_count} ders</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <UserRowActions
                      u={u}
                      me={me}
                      onExtend={() => setExtendTarget(u)}
                      onRecordPayment={() => setPaymentTarget(u)}
                      onBlock={() => block(u)}
                      onUnblock={() => unblock(u)}
                      onMakeAdmin={() => makeAdmin(u)}
                      onRemoveAdmin={() => removeAdmin(u)}
                      onDelete={() => del(u)}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm">Kullanıcı bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
          {/* Mobile card list */}
          <ul className="md:hidden divide-y divide-border">
            {filtered.map((u) => (
              <li key={u.id} className="p-3" data-testid={`admin-user-card-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <Pill status={u.plan_status} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {u.subscription_ends_at ? `Abonelik: ${formatDate(u.subscription_ends_at)}` : u.trial_ends_at ? `Deneme: ${formatDate(u.trial_ends_at)}` : "—"}
                  {" · "}{u.student_count} öğrenci
                </div>
                <div className="mt-2">
                  <UserRowActions
                    u={u}
                    me={me}
                    onExtend={() => setExtendTarget(u)}
                    onRecordPayment={() => setPaymentTarget(u)}
                    onBlock={() => block(u)}
                    onUnblock={() => unblock(u)}
                    onMakeAdmin={() => makeAdmin(u)}
                    onRemoveAdmin={() => removeAdmin(u)}
                    onDelete={() => del(u)}
                  />
                </div>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="py-10 text-center text-muted-foreground text-sm">Kullanıcı bulunamadı.</li>
            )}
          </ul>
        </div>
      )}

      <ExtendDialog target={extendTarget} onClose={() => setExtendTarget(null)} onSaved={load} />
      <RecordPaymentDialog target={paymentTarget} onClose={() => setPaymentTarget(null)} onSaved={load} />
    </div>
  );
}

function UserRowActions({ u, me, onExtend, onRecordPayment, onBlock, onUnblock, onMakeAdmin, onRemoveAdmin, onDelete }) {
  const isSelf = me && u.id === me.id;
  return (
    <div className="flex flex-wrap gap-1 justify-end">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRecordPayment} data-testid={`user-action-payment-${u.id}`}>
        <Receipt size={12} className="mr-1" /> Ödeme
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onExtend} data-testid={`user-action-extend-${u.id}`}>
        <Clock size={12} className="mr-1" /> Uzat
      </Button>
      {u.is_blocked ? (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onUnblock} data-testid={`user-action-unblock-${u.id}`}>
          <CheckCircle size={12} className="mr-1" /> Engeli Kaldır
        </Button>
      ) : u.role !== "admin" ? (
        <Button size="sm" variant="outline" className="h-7 text-xs text-[#C62828]" onClick={onBlock} data-testid={`user-action-block-${u.id}`}>
          <Prohibit size={12} className="mr-1" /> Engelle
        </Button>
      ) : null}
      {u.role === "admin" ? (
        !isSelf && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRemoveAdmin} data-testid={`user-action-remove-admin-${u.id}`}>
            <X size={12} className="mr-1" /> Admin'den çıkar
          </Button>
        )
      ) : (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onMakeAdmin} data-testid={`user-action-make-admin-${u.id}`}>
          <ShieldCheck size={12} className="mr-1" /> Admin yap
        </Button>
      )}
      {!isSelf && (
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#C62828]" onClick={onDelete} data-testid={`user-action-delete-${u.id}`} title="Sil">
          <Trash size={14} />
        </Button>
      )}
    </div>
  );
}

function ExtendDialog({ target, onClose, onSaved }) {
  const [months, setMonths] = useState(12);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) { setMonths(12); setNote(""); } }, [target]);
  async function save() {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`/admin/users/${target.id}/extend`, { months: Number(months), note });
      toast.success(`${months} ay uzatıldı.`);
      onSaved?.(); onClose();
    } catch { toast.error("Başarısız."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Aboneliği uzat</DialogTitle></DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{target.email}</div>
            <div className="space-y-1.5">
              <Label>Kaç ay?</Label>
              <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                <SelectTrigger data-testid="extend-months"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 3, 6, 12, 24].map((m) => <SelectItem key={m} value={String(m)}>{m} ay</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Not (opsiyonel)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="örn: müşteri talebiyle" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="extend-confirm">Uzat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: "", months: 12, method: "bank_transfer", note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (target) {
      api.get("/admin/settings").then((r) => setForm((f) => ({ ...f, amount: String(r.data.annual_price || "") }))).catch(() => {});
    }
  }, [target]);
  async function save() {
    if (!target) return;
    if (!form.amount) return toast.error("Tutar gerekli.");
    setSaving(true);
    try {
      await api.post(`/admin/users/${target.id}/payment`, {
        amount: Number(form.amount),
        months: Number(form.months),
        method: form.method,
        note: form.note,
      });
      toast.success("Ödeme kaydedildi ve abonelik uzatıldı.");
      onSaved?.(); onClose();
    } catch { toast.error("Başarısız."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Ödeme kaydet</DialogTitle></DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{target.email}</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tutar</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Uzatma (ay)</Label>
                <Select value={String(form.months)} onValueChange={(v) => setForm({ ...form, months: Number(v) })}>
                  <SelectTrigger data-testid="payment-months"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 3, 6, 12, 24].map((m) => <SelectItem key={m} value={String(m)}>{m} ay</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Yöntem</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Havale / EFT</SelectItem>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Not</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="örn: EFT ref 123456" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="payment-confirm">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Pricing Tab --------
function PricingTab() {
  const [form, setForm] = useState({
    annual_price: 1200,
    currency: "TRY",
    bank_name: "",
    bank_iban: "",
    bank_account_holder: "",
    payment_note: "",
  });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/admin/settings");
      setForm({
        annual_price: r.data.annual_price ?? 1200,
        currency: r.data.currency || "TRY",
        bank_name: r.data.bank_name || "",
        bank_iban: r.data.bank_iban || "",
        bank_account_holder: r.data.bank_account_holder || "",
        payment_note: r.data.payment_note || "",
      });
    } catch { toast.error("Ayarlar yüklenemedi."); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { ...form, annual_price: Number(form.annual_price) });
      toast.success("Fiyatlandırma kaydedildi.");
      load();
    } catch { toast.error("Kaydedilemedi."); }
    finally { setSaving(false); }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Plan Fiyatı</h2>
        <div className="grid sm:grid-cols-[1fr_140px] gap-3">
          <div className="space-y-1.5">
            <Label>Yıllık fiyat</Label>
            <Input
              type="number"
              step="0.01"
              value={form.annual_price}
              onChange={(e) => setForm({ ...form, annual_price: e.target.value })}
              data-testid="pricing-annual-price"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Para birimi</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="pricing-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["TRY", "USD", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Bu fiyat, süresi dolmuş kullanıcılara ödeme ekranında gösterilir.
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Banka / Havale Bilgileri</h2>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Banka adı</Label>
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="örn: Garanti BBVA" data-testid="pricing-bank-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Hesap sahibi</Label>
            <Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} placeholder="örn: Ali Sevinç" data-testid="pricing-account-holder" />
          </div>
          <div className="space-y-1.5">
            <Label>IBAN</Label>
            <Input value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value })} placeholder="TR00 0000 0000 0000 0000 0000 00" data-testid="pricing-iban" className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label>Ödeme notu</Label>
            <Input value={form.payment_note} onChange={(e) => setForm({ ...form, payment_note: e.target.value })} placeholder="Açıklamaya e-postanızı yazın" data-testid="pricing-note" />
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="pricing-save">
        <PencilSimple size={14} className="mr-1" /> {saving ? "Kaydediliyor…" : "Kaydet"}
      </Button>
    </div>
  );
}

// -------- Stats Tab --------
function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => toast.error("İstatistikler yüklenemedi."));
  }, []);
  if (!s) return <div className="py-10 text-center text-muted-foreground text-sm">Yükleniyor…</div>;
  const c = s.currency || "TRY";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Toplam kullanıcı" value={s.total_users} testid="stat-total-users" />
        <StatBox label="Aktif abone" value={s.active_subscribers} tone="success" testid="stat-active" />
        <StatBox label="Deneme kullanıcısı" value={s.trial_users} tone="accent" testid="stat-trial" />
        <StatBox label="Son 30 gün kayıt" value={s.new_signups_30d} testid="stat-new" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatBox label="Toplam gelir" value={formatCurrency(s.total_revenue, c)} tone="primary" testid="stat-revenue" />
        <StatBox label="Son 30 gün gelir" value={formatCurrency(s.revenue_last_30d, c)} testid="stat-revenue-30" />
        <StatBox label="Öngörülen ARR" value={formatCurrency(s.projected_arr, c)} tone="primary" testid="stat-arr" />
      </div>

      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-display font-bold mb-3">Aylık Gelir (Son 12 Ay)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s.monthly_revenue}>
              <CartesianGrid stroke="#E5E4E0" vertical={false} />
              <XAxis dataKey="month" stroke="#5C6A5D" fontSize={11} />
              <YAxis stroke="#5C6A5D" fontSize={11} />
              <Tooltip formatter={(v) => formatCurrency(v, c)} contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} />
              <Bar dataKey="revenue" fill="#3A5A40" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone, testid }) {
  const tones = {
    primary: "bg-[#3A5A40] text-white",
    success: "bg-[#E8F5E9] text-[#2E7D32]",
    accent: "bg-[#FFF3E0] text-[#E65100]",
  };
  return (
    <div className={`rounded-lg border border-border p-4 ${tones[tone] || "bg-white"}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
